import Markdown from 'react-markdown'
import Prism from 'prismjs'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { FC, useMemo, useState } from 'react'
import { getLastBoldSegment, isKeyQuestion } from 'helpers'

type Props = {
  content: string
  initialExpandedState?: boolean
  showButtonClassName?: string
  onExpandClick?: (expanded: boolean) => void
}

const CHARACTER_LIMIT = 320

const MarkdownMessage: FC<Props> = ({
  content,
  initialExpandedState = false,
  showButtonClassName = 'text-primary hover:text-primary/80',
  onExpandClick
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpandedState)

  const shouldShowExpandButton = useMemo(() => {
    const trimmedContent = content.trim()
    if (trimmedContent.length <= CHARACTER_LIMIT) {
      return false
    }
    const segment = getLastBoldSegment(trimmedContent)
    if (segment && isKeyQuestion(trimmedContent, segment.start, segment.end)) {
      if (segment.start < CHARACTER_LIMIT && CHARACTER_LIMIT < segment.end) {
        return false
      }
    }
    return true
  }, [content])

  const memoizedContent = useMemo(() => {
    const trimmedContent = content.trim()
    if (!shouldShowExpandButton) return trimmedContent
    return isExpanded ? trimmedContent : trimmedContent.slice(0, CHARACTER_LIMIT) + '...'
  }, [content, isExpanded, shouldShowExpandButton])

  const handleExpandedClick = () => {
    const newValue = !isExpanded
    setIsExpanded(newValue)
    onExpandClick?.(newValue)
  }

  return (
    <div className="flex flex-col gap-2 markdown-block">
      <Markdown
        skipHtml={false}
        className="flex flex-col gap-2.5"
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
          code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '')
            return match ? (
              <pre className={className}>
                <code
                  className={`language-${match[1]}`}
                  dangerouslySetInnerHTML={{
                    __html: Prism.highlight(String(children), Prism.languages['javascript'], 'javascript')
                  }}
                />
              </pre>
            ) : null
          }
        }}
      >
        {memoizedContent}
      </Markdown>
      {shouldShowExpandButton && (
        <Button
          type="link"
          className={`p-0 h-auto text-xs self-end ${showButtonClassName}`}
          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
          onClick={handleExpandedClick}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  )
}

export default MarkdownMessage
